import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Plus, Download, Upload, ExternalLink, Save, X,
  CheckCircle2, Archive, AlertTriangle, Loader2,
} from "lucide-react";
import {
  adminStatisticsList, adminStatisticsCreate, adminStatisticsUpdate,
  adminStatisticsPublish, adminStatisticsArchive,
  adminStatisticsImport, adminStatisticsExport,
} from "../lib/api";

const COUNTRY_LABEL = { DE: "Deutschland", AT: "Österreich", CH: "Schweiz" };
const TOPIC_LABEL = {
  marriage: "Ehe", divorce: "Scheidung",
  households: "Haushalte", family_children: "Familie & Kinder",
};
const STATE_META = {
  draft: { c: "bg-white/10 text-white/70 border-white/20", label: "Entwurf" },
  review: { c: "bg-amber-500/15 text-amber-200 border-amber-400/30", label: "In Prüfung" },
  published: { c: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30", label: "Veröffentlicht" },
  archived: { c: "bg-white/5 text-white/40 border-white/10", label: "Archiviert" },
};

const EMPTY_STAT = {
  country: "DE", topic: "marriage", indicator_key: "",
  label: "", value_numeric: 0, unit: "count",
  reference_period: "2025", publication_date: "",
  publication_status: "definitive",
  source_agency: "", source_title: "", source_url: "",
  definition: "", caveat: "", comparability: "broadly_comparable",
  featured: false, source_checked_at: new Date().toISOString().slice(0, 10),
  publication_state: "review",
};


function daysSince(dateStr) {
  if (!dateStr) return null;
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
}


function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-white/50">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-white/40 mt-1">{hint}</span>}
    </label>
  );
}


function StatDrawer({ stat, onClose, onSaved }) {
  const [form, setForm] = useState(stat || EMPTY_STAT);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isNew = !stat?.id;

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked
      : e.target.type === "number" ? Number(e.target.value)
      : e.target.value;
    setForm({ ...form, [k]: v });
  };

  async function save() {
    setBusy(true); setErr("");
    try {
      const payload = { ...form };
      if (isNew) {
        await adminStatisticsCreate(payload);
      } else {
        // nur veränderte Felder patchen (server verlangt Pydantic-Validierung)
        await adminStatisticsUpdate(stat.id, payload);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    } finally { setBusy(false); }
  }

  async function publish() {
    setBusy(true); setErr("");
    try {
      await adminStatisticsPublish(stat.id, "admin ui");
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : JSON.stringify(d));
    } finally { setBusy(false); }
  }

  async function archive() {
    setBusy(true); setErr("");
    try {
      await adminStatisticsArchive(stat.id, "admin ui");
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end"
         data-testid="stat-drawer">
      <div className="w-full max-w-2xl bg-[#141822] border-l border-[#D4AF37]/30 h-full overflow-y-auto p-6 text-[#F5EFE7]">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/70 mb-1">
              {isNew ? "Neue Kennzahl" : "Kennzahl bearbeiten"}
            </div>
            <h2 className="text-xl font-serif">
              {isNew ? "Anlegen" : `${COUNTRY_LABEL[form.country]} · ${form.label}`}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="drawer-close">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="Land">
            <select value={form.country} onChange={update("country")}
              data-testid="drawer-country"
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm">
              {Object.entries(COUNTRY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Thema">
            <select value={form.topic} onChange={update("topic")}
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm">
              {Object.entries(TOPIC_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="indicator_key">
            <input value={form.indicator_key} onChange={update("indicator_key")} disabled={!isNew}
              data-testid="drawer-indicator-key"
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm disabled:opacity-60" />
          </Field>
          <Field label="Label">
            <input value={form.label} onChange={update("label")}
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm" />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <Field label="Wert">
            <input type="number" step="any" value={form.value_numeric} onChange={update("value_numeric")}
              data-testid="drawer-value"
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Einheit">
            <select value={form.unit} onChange={update("unit")}
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm">
              <option value="count">count</option>
              <option value="percent">percent</option>
              <option value="percent_of_private_households">% of households</option>
              <option value="children_per_woman">children/woman</option>
            </select>
          </Field>
          <Field label="Referenzjahr">
            <input value={form.reference_period} onChange={update("reference_period")}
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="Veröff. am">
            <input value={form.publication_date} onChange={update("publication_date")} placeholder="YYYY-MM-DD"
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Quellprüfung am">
            <input value={form.source_checked_at} onChange={update("source_checked_at")} placeholder="YYYY-MM-DD"
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="Publikationsstatus (definitiv/provisorisch)">
            <select value={form.publication_status} onChange={update("publication_status")}
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm">
              <option value="definitive">definitive</option>
              <option value="provisional">provisional</option>
            </select>
          </Field>
          <Field label="Vergleichbarkeit">
            <select value={form.comparability} onChange={update("comparability")}
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm">
              <option value="broadly_comparable">broadly_comparable</option>
              <option value="qualified_comparison">qualified_comparison</option>
              <option value="context_only">context_only (kein Ranking)</option>
            </select>
          </Field>
        </div>

        <Field label="Quelltitel">
          <input value={form.source_title} onChange={update("source_title")}
            className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm mb-3" />
        </Field>

        <Field label="Behörde (Quelle)">
          <input value={form.source_agency} onChange={update("source_agency")}
            className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm mb-3" />
        </Field>

        <Field label="Quell-URL (HTTPS · nur destatis/statistik.at/bfs.admin.ch)"
               hint="Wird serverseitig gegen ALLOWED_DOMAINS validiert.">
          <input value={form.source_url} onChange={update("source_url")}
            data-testid="drawer-source-url"
            className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm mb-3" />
        </Field>

        <Field label="Definition">
          <textarea value={form.definition} onChange={update("definition")} rows={3}
            className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm mb-3" />
        </Field>

        <Field label="Caveat (methodischer Hinweis)">
          <textarea value={form.caveat || ""} onChange={update("caveat")} rows={2}
            className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm mb-3" />
        </Field>

        <label className="flex items-center gap-2 text-sm mb-4">
          <input type="checkbox" checked={!!form.featured} onChange={update("featured")}
            data-testid="drawer-featured" />
          <span>Featured (auf DACH-Übersicht anzeigen)</span>
        </label>

        {err && <div className="text-rose-300 text-sm mb-3" data-testid="drawer-error">{err}</div>}

        <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10">
          <button onClick={save} disabled={busy}
                  data-testid="drawer-save"
                  className="px-4 py-2 bg-[#D4AF37] text-black rounded text-sm hover:bg-[#c19f2a] disabled:opacity-40 inline-flex items-center gap-1.5">
            {busy && <Loader2 size={12} className="animate-spin" />}
            <Save size={12} /> Speichern
          </button>
          {!isNew && form.publication_state !== "published" && (
            <button onClick={publish} disabled={busy}
                    data-testid="drawer-publish"
                    className="px-4 py-2 bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 rounded text-sm hover:bg-emerald-500/30 inline-flex items-center gap-1.5">
              <CheckCircle2 size={12} /> Publizieren
            </button>
          )}
          {!isNew && form.publication_state === "published" && (
            <button onClick={archive} disabled={busy}
                    data-testid="drawer-archive"
                    className="px-4 py-2 bg-white/5 border border-white/20 text-white/70 rounded text-sm hover:bg-white/10 inline-flex items-center gap-1.5">
              <Archive size={12} /> Archivieren
            </button>
          )}
          <button onClick={onClose} className="ml-auto px-4 py-2 border border-white/20 text-white/60 rounded text-sm">
            Schliessen
          </button>
        </div>
      </div>
    </div>
  );
}


function ImportDialog({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function run(dryRun) {
    setBusy(true); setErr(""); setReport(null);
    let items;
    try {
      items = JSON.parse(text);
      if (!Array.isArray(items)) throw new Error("JSON muss ein Array sein");
    } catch (e) { setErr(String(e)); setBusy(false); return; }
    try {
      const rep = await adminStatisticsImport(items, dryRun);
      setReport(rep);
      if (!dryRun && rep.errors.length === 0) {
        setTimeout(onDone, 800);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
         data-testid="import-dialog">
      <div className="max-w-2xl w-full bg-[#141822] border border-[#D4AF37]/30 rounded-lg p-6 text-[#F5EFE7]">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-serif">JSON-Import</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="import-close">
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-white/50 mb-3">
          Array von Statistik-Objekten. Importierte Änderungen starten als
          &quot;review&quot; — nicht automatisch publiziert.
        </p>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={12}
          data-testid="import-textarea"
          placeholder='[{"country":"DE","topic":"marriage",...}]'
          className="w-full bg-black/40 border border-white/20 rounded px-2 py-2 text-xs font-mono" />

        {err && <div className="text-rose-300 text-sm mt-3" data-testid="import-error">{err}</div>}

        {report && (
          <div className="mt-3 border border-white/10 rounded p-3 text-xs" data-testid="import-report">
            <div>validated: {report.validated} · would_create: {report.would_create} · would_update: {report.would_update}</div>
            {report.errors?.length > 0 && (
              <ul className="mt-2 text-rose-300">
                {report.errors.map((e, i) => <li key={i}>#{e.index} · {e.error}</li>)}
              </ul>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <button onClick={() => run(true)} disabled={busy || !text}
                  data-testid="import-dry-run"
                  className="px-4 py-2 border border-white/20 text-white/80 rounded text-sm inline-flex items-center gap-1.5">
            {busy && <Loader2 size={12} className="animate-spin" />} Dry Run
          </button>
          <button onClick={() => run(false)} disabled={busy || !text || !report || report.errors?.length > 0}
                  data-testid="import-execute"
                  className="px-4 py-2 bg-[#D4AF37] text-black rounded text-sm hover:bg-[#c19f2a] disabled:opacity-40">
            Import ausführen (als review)
          </button>
          <button onClick={onClose} className="ml-auto px-4 py-2 text-white/50 text-sm">Abbrechen</button>
        </div>
      </div>
    </div>
  );
}


export default function AdminStatistics() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState({ country: "", topic: "", publication_state: "" });
  const [selected, setSelected] = useState(null);      // stat object (edit) or {} for new
  const [importOpen, setImportOpen] = useState(false);

  async function load() {
    setLoading(true); setErr("");
    try {
      const data = await adminStatisticsList(
        Object.fromEntries(Object.entries(filter).filter(([, v]) => v)),
      );
      setItems(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [filter.country, filter.topic, filter.publication_state]);

  async function doExport() {
    try {
      const data = await adminStatisticsExport();
      const blob = new Blob([JSON.stringify(data.items, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `dach-statistics-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { setErr(String(e)); }
  }

  const total = items.length;
  const published = items.filter(i => i.publication_state === "published").length;
  const staleThreshold = 18 * 30; // ca. 18 Monate

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-[#F5EFE7] pt-24" data-testid="admin-statistics-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white/80 mb-6">
          <ArrowLeft size={14} /> Admin-Dashboard
        </Link>

        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/70 mb-1">Admin</div>
            <h1 className="text-3xl font-serif">DACH-Gesellschaftsbarometer</h1>
            <p className="text-sm text-white/50 mt-2">
              {total} Kennzahlen · {published} publiziert. Jede Änderung wird auditiert.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setImportOpen(true)}
              data-testid="btn-import"
              className="px-3 py-2 border border-white/20 text-white/80 rounded text-sm inline-flex items-center gap-1.5">
              <Upload size={12} /> Import
            </button>
            <button onClick={doExport}
              data-testid="btn-export"
              className="px-3 py-2 border border-white/20 text-white/80 rounded text-sm inline-flex items-center gap-1.5">
              <Download size={12} /> Export
            </button>
            <button onClick={() => setSelected(EMPTY_STAT)}
              data-testid="btn-new"
              className="px-3 py-2 bg-[#D4AF37] text-black rounded text-sm inline-flex items-center gap-1.5 hover:bg-[#c19f2a]">
              <Plus size={12} /> Neu
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <select value={filter.country} onChange={e => setFilter({ ...filter, country: e.target.value })}
                  data-testid="filter-country"
                  className="bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm">
            <option value="">Alle Länder</option>
            {Object.entries(COUNTRY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filter.topic} onChange={e => setFilter({ ...filter, topic: e.target.value })}
                  data-testid="filter-topic"
                  className="bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm">
            <option value="">Alle Themen</option>
            {Object.entries(TOPIC_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filter.publication_state} onChange={e => setFilter({ ...filter, publication_state: e.target.value })}
                  data-testid="filter-state"
                  className="bg-black/40 border border-white/20 rounded px-2 py-1.5 text-sm">
            <option value="">Alle Status</option>
            {Object.keys(STATE_META).map(k => <option key={k} value={k}>{STATE_META[k].label}</option>)}
          </select>
        </div>

        {loading && <div className="text-white/50 text-sm">Lade …</div>}
        {err && <div className="text-rose-300 text-sm">{err}</div>}

        {/* Table */}
        <div className="border border-white/10 rounded-lg overflow-hidden" data-testid="stats-table">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/50 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Land</th>
                <th className="text-left p-3">Kennzahl</th>
                <th className="text-left p-3">Wert</th>
                <th className="text-left p-3">Jahr</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Featured</th>
                <th className="text-left p-3">Quelle geprüft</th>
                <th className="text-left p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => {
                const meta = STATE_META[i.publication_state] || STATE_META.review;
                const stale = daysSince(i.source_checked_at);
                return (
                  <tr key={i.id} className="border-t border-white/5 hover:bg-white/[0.02]"
                      data-testid={`row-${i.id}`}>
                    <td className="p-3 text-white/80">{COUNTRY_LABEL[i.country]}</td>
                    <td className="p-3">
                      <div className="text-white/90">{i.label}</div>
                      <div className="text-[10px] text-white/40">{i.indicator_key}</div>
                    </td>
                    <td className="p-3 text-white/90 font-mono">
                      {i.value_numeric.toLocaleString("de-CH")}
                    </td>
                    <td className="p-3 text-white/60">{i.reference_period}</td>
                    <td className="p-3">
                      <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${meta.c}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="p-3 text-white/60">{i.featured ? "✓" : "—"}</td>
                    <td className="p-3 text-xs">
                      <span className={stale !== null && stale > staleThreshold ? "text-amber-300" : "text-white/50"}>
                        {i.source_checked_at || "—"}
                        {stale !== null && stale > staleThreshold && (
                          <AlertTriangle size={11} className="inline ml-1" />
                        )}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-2">
                        <a href={i.source_url} target="_blank" rel="noopener noreferrer"
                           className="text-[#D4AF37]/80 hover:text-[#D4AF37]" title="Quelle öffnen">
                          <ExternalLink size={14} />
                        </a>
                        <button onClick={() => setSelected(i)}
                                data-testid={`edit-${i.id}`}
                                className="text-white/70 hover:text-white text-xs px-2 py-1 border border-white/15 rounded">
                          Bearbeiten
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selected && (
          <StatDrawer
            stat={selected.id ? selected : null}
            onClose={() => setSelected(null)}
            onSaved={() => { setSelected(null); load(); }}
          />
        )}
        {importOpen && (
          <ImportDialog
            onClose={() => setImportOpen(false)}
            onDone={() => { setImportOpen(false); load(); }}
          />
        )}
      </div>
    </div>
  );
}
